/* eslint-disable react/prop-types */
import { useContext } from 'react';
import uniqid from 'uniqid';
import { StoreContext } from '../../store';
import passProps from '../../utils/passProps';

const getFilteredElementsData = ({ elements, filters }) => {
  const state = filters;
  const stateKeys = Object.keys(state);

  const allFiltersEmpty = stateKeys.every(
    (key) => Array.isArray(state[key]) && !Object.keys(state[key]).length,
  );
  const allFilterValuesFalse = stateKeys.every(
    (key) =>
      Array.isArray(state[key]) &&
      Object.values(state[key]).every((value) => !value),
  );

  const contentTypeCount = {};

  const filteredElements = elements.filter((child) => {
    if (allFiltersEmpty || allFilterValuesFalse) {
      // If no filters are applied, count all content types
      child.props?._contentTypes?.data.forEach(({ uniqueID }) => {
        contentTypeCount[uniqueID] = (contentTypeCount[uniqueID] || 0) + 1;
      });
      return true;
    }

    const elementData = {};
    Object.values(child.props).forEach((value) => {
      if (value && value.mainKey) {
        elementData[value.mainKey] = value.data.map(({ uniqueID }) => uniqueID);
      }
    });

    const hasSomeOfEach = stateKeys.every((key) => {
      const elementDataValue = elementData[key];
      const allTagsFalse = Object.values(state[key]).every((value) => !value);
      const stateHasNoTags = Object.entries(state[key]).length === 0;

      if (stateHasNoTags || allTagsFalse) {
        return true;
      }

      return Object.entries(state[key]).some(([tag, value]) => {
        if (value === false) {
          return false;
        }

        const hasTag =
          elementDataValue !== null || elementDataValue !== undefined;
        const tagArrayIsEmpty =
          Array.isArray(elementDataValue || []) &&
          (elementDataValue || []).length === 0;

        if (!hasTag || tagArrayIsEmpty) {
          return false;
        }

        return (
          elementDataValue.includes(tag) ||
          elementDataValue.includes(Number(tag))
        );
      });
    });

    if (hasSomeOfEach) {
      // Update content type count
      child.props?._contentTypes?.data.forEach(({ uniqueID }) => {
        contentTypeCount[uniqueID] = (contentTypeCount[uniqueID] || 0) + 1;
      });
    }

    return hasSomeOfEach;
  });

  return { filteredElements, contentTypeCount };
};

const collectTagState = (elements, query) => {
  const stateSets = {};
  const findInternalAttrs = (elems) => {
    elems.forEach(({ props }) => {
      const { elements: nestedElements } = props;

      const internalAttrs = Object.entries(props).filter(([key]) =>
        key.startsWith('_'),
      );

      internalAttrs.forEach(([, value]) => {
        const { mainKey: key, queryKey, data } = value;
        const queryParams = query[queryKey]?.split(',') || [];

        if (!stateSets[key]) {
          stateSets[key] = {
            key,
            queryKey,
            query: new Set(),
            collected: new Set(),
          };
        }

        data.forEach(({ uniqueID, shortID }) => {
          const uId = uniqueID.toString();
          const sId = shortID.toString();

          if (!stateSets[key].collected.has(uId) && queryParams.includes(sId)) {
            stateSets[key].query.add(uId);
          }

          stateSets[key].collected.add(uId);
        });
      });

      const flatElements = (nestedElements || []).flat();
      findInternalAttrs(flatElements);
    });
  };

  findInternalAttrs(elements);

  return Object.entries(stateSets).reduce((acc, [key, value]) => {
    const parsedQueryParams = Array.from(value.query).map((uniqueID) => [
      uniqueID,
      true,
    ]);

    const parsedCollected = Array.from(value.collected).map((uniqueID) => [
      uniqueID,
      false,
    ]);
    return {
      ...acc,
      [key]: {
        ...Object.fromEntries(parsedCollected),
        ...Object.fromEntries(parsedQueryParams),
      },
    };
  }, {});
};

const PureTagPanel = ({
  elements = [],
  topItemId,
  control: controlSlot = null,
  query = {},
  fallback,
}) => {
  const filterObjectByKeys = (obj, keysToKeep) => {
    const result = {};

    keysToKeep.forEach((key) => {
      if (Object.prototype.hasOwnProperty.call(obj, key)) {
        result[key] = obj[key];
      }
    });
    return result;
  };

  const state = collectTagState(elements, query);
  const filterState = filterObjectByKeys(state, [
    'classifications',
    'geodata',
    'hotelproducts',
  ]);
  const tabFilterState = filterObjectByKeys(state, ['contentTypes']);

  const [controlItem] = controlSlot;
  const controlUmbracoContentIdFormatted = controlItem?.props?.id?.replaceAll(
    '-',
    '_',
  );
  const tagPanelWindowScriptId = `__IDUN_TAG_PANEL_${
    controlUmbracoContentIdFormatted || uniqid
  }__`;

  const { initStore } = useContext(StoreContext);
  const store = initStore(topItemId, {
    filters: filterState,
    tabFilters: tabFilterState,
  });

  const handleOnGetFilteredElements = (elems) => {
    const { filters, tabFilters } = store.get();

    // TODO: Fix content type counter to avoid having to filter twice
    const { filteredElements, contentTypeCount } = getFilteredElementsData({
      elements: elems,
      filters,
    });

    const { filteredElements: elementsFilteredByTabs } =
      getFilteredElementsData({
        elements: filteredElements,
        filters: tabFilters,
      });

    return { filteredElements: elementsFilteredByTabs, contentTypeCount };
  };

  const renderControlSlot = () => {
    return passProps(controlItem, { keyPrefix: 'tag-panel-control-slot-' });
  };

  const renderContentSlot = () => {
    const props = {
      getFilteredElementsData: handleOnGetFilteredElements,
      fallback,
    };
    return passProps(elements, { propsToPass: props });
  };

  return (
    <>
      {renderControlSlot()}
      <div id={topItemId} style={{ display: 'flex', flexDirection: 'column' }}>
        {renderContentSlot()}
      </div>
      {typeof window === 'undefined' && (
        <script
          dangerouslySetInnerHTML={{
            __html: `window.${tagPanelWindowScriptId} = ${JSON.stringify({
              topItemId,
            })};`,
          }}
        />
      )}
    </>
  );
};
PureTagPanel.app = 'idun';
PureTagPanel.displayName = 'PureTagPanel';

export default PureTagPanel;
